import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuArrowRight } from "react-icons/lu";
import HeaderOne from "../../header/HeaderOne";
import FooterOne from "../../footer/FooterOne";
import Head from "../../components/Head";
import "./BlogPages.css";

// Read time estimator — assumes ~220 words/min, falls back to 3 min if no body.
const estimateReadTime = (post) => {
  const text = (post.body || post.content || post.subtitle || '').toString();
  if (!text) return 3;
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};

// Lightly group posts by category, falling back to "Guides" if none set.
const groupByCategory = (posts) => {
  const groups = {};
  for (const p of posts) {
    const cat = (p.category || 'Guides').trim() || 'Guides';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(p);
  }
  // Stable order: known categories first, others alphabetically
  const order = ['Setup', 'Carrier data', 'Integrations', 'Use cases', 'Guides'];
  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });
  return sorted.map((cat) => ({ category: cat, posts: groups[cat] }));
};

export default function BlogPages() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${global.SERVIP}/blog-controller`);
        if (!response.ok) throw new Error('Failed to fetch guides');
        const data = await response.json();
        setPosts(Array.isArray(data) ? data : data.blogs || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const groups = groupByCategory(posts);

  return (
    <>
      <Head
        title="SMS Gateway Setup Guides & Carrier Delivery Data"
        description="Setup walkthroughs, carrier delivery rates by country, integration tutorials, and field-tested guides for getting the most out of your TextLink SMS gateway."
        canonical="/guides"
      />
      <HeaderOne />

      <main className="gd">
        <div className="gd-shell">

          {/* ---- Hero ---- */}
          <header className="gd-hero">
            <span className="gd-eyebrow">Guides</span>
            <h1 className="gd-h1">How to actually use TextLink</h1>
            <p className="gd-lead">
              Setup walkthroughs, carrier delivery data by country, integration tutorials, and field-tested guides for getting the most out of your SMS gateway.
            </p>
          </header>

          {/* ---- Loading ---- */}
          {loading && (
            <section className="gd-section">
              <p className="gd-empty">Loading guides…</p>
            </section>
          )}

          {/* ---- Error ---- */}
          {error && !loading && (
            <section className="gd-section">
              <p className="gd-empty gd-empty--err">
                Couldn't load guides right now. Try refreshing — or come back in a moment.
              </p>
            </section>
          )}

          {/* ---- Empty ---- */}
          {!loading && !error && posts.length === 0 && (
            <section className="gd-section">
              <p className="gd-empty">No guides published yet. Check back soon.</p>
            </section>
          )}

          {/* ---- Categorized grid ---- */}
          {!loading && !error && posts.length > 0 && groups.map(({ category, posts: cposts }) => (
            <section key={category} className="gd-section">
              <h2 className="gd-h2">{category}</h2>
              <div className="gd-grid">
                {cposts.map((post) => {
                  const minutes = estimateReadTime(post);
                  return (
                    <Link
                      key={post.id}
                      to={`/${post.slug}`}
                      state={{ allBlogs: posts }}
                      className="gd-card"
                    >
                      <h3 className="gd-card-title">{post.title}</h3>
                      {post.subtitle && (
                        <p className="gd-card-sub">{post.subtitle}</p>
                      )}
                      <div className="gd-card-meta">
                        <span>{minutes} min read</span>
                        <LuArrowRight size={14} strokeWidth={2} className="gd-card-arrow" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}

          {/* ---- Final CTA ---- */}
          <section className="gd-cta">
            <h2 className="gd-h2">Ready to send your first SMS?</h2>
            <p className="gd-sub">Five minutes from signup to first message. 300 free messages, no credit card.</p>
            <Link to="/signup" className="gd-btn">
              Try free <LuArrowRight size={15} strokeWidth={2} />
            </Link>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
